/**
 * Experiment C — Surface coupling.
 *
 * Near a coupling surface, an attraction toward the surface acts in
 * proportion to proximity and to the tangential speed along it:
 *   a = -n · strength · w(d) · s_t / (s_t + speedRef)
 * Fast, tangential motion "holds" to curved surfaces (e.g. over a hump or
 * around a curved wall); slow or head-on motion is barely affected. Agency:
 * the coupling switches off while the player steers away from a wall-like
 * surface, and for a short time after any jump, so separation is always a
 * player choice.
 *
 * Work-free (Phase 2D): the summed pull a is applied only perpendicular to
 * the velocity v, plus a second-order correction along v,
 *   a' = a⊥ − v̂ · |a⊥|²·dt / (2|v|),   a⊥ = a − (a·v̂)v̂
 * so |v + a'dt|² = |v|² + O(dt⁴): coupling redirects motion (holds you to
 * a crest or wall) but never speeds you up or slows you down. The original
 * work-doing pull let a ground pass past a coupling column exit 0.6–1.0 m/s
 * faster than baseline and bank the gain with a jump, a repeatable speed
 * pump. It was removed in Phase 3F so no tuning can bring it back
 * (research/INTERACTIONS.md R7, research/EXPERIMENT_REVIEW.md).
 */
import { Vec3 } from '../../core/Vec3';
import type { FieldRule } from '../MovementRules';
import { proximityWeight, surfaceStrength } from './surfaceFilter';

export interface CouplingParams extends Record<string, number> {
  range: number;
  strength: number;
  speedRef: number;
  minTangentialSpeed: number;
  separateInput: number;
  jumpReleaseTicks: number;
  allSurfaces: number;
}

const WALL_UP_DOT = 0.5;
const tangential = new Vec3();
const planarN = new Vec3();
const pull = new Vec3();
const vHat = new Vec3();

export function createCouplingRule(): FieldRule<CouplingParams> {
  return {
    id: 'exp.coupling',
    description: 'C: attraction toward nearby coupling surfaces that grows with tangential speed.',
    stage: 'field',
    order: 10,
    conflictsWith: [],
    energy: 'conserve',
    params: { range: 1.2, strength: 80, speedRef: 10, minTangentialSpeed: 4, separateInput: 0.3, jumpReleaseTicks: 12, allSurfaces: 0 },
    paramSpecs: {
      range: { min: 0.1, max: 5, description: 'Distance (m) over which coupling acts' },
      strength: { min: 0, max: 200, description: 'Max attraction (m/s²)' },
      speedRef: { min: 0.1, max: 100, description: 'Tangential speed at which coupling is half strength' },
      minTangentialSpeed: { min: 0, max: 50, description: 'Below this tangential speed, no coupling' },
      separateInput: { min: -1, max: 1, description: 'Steering away beyond this alignment disables coupling' },
      jumpReleaseTicks: { min: 0, max: 120, description: 'Ticks after a jump with no coupling' },
      allSurfaces: { min: 0, max: 1, description: 'Strength on non-coupling surfaces' },
    },
    proximityRange: (p) => p.range,
    applies: (ctx, p) => ctx.state.alive && ctx.nearbyCount > 0 && ctx.state.ticksSinceJump > p.jumpReleaseTicks,
    accumulate(ctx, p, out) {
      const v = ctx.state.velocity;
      pull.set(0, 0, 0);
      for (let i = 0; i < ctx.nearbyCount; i++) {
        const c = ctx.nearby[i]!;
        const k = surfaceStrength(c, 'coupling', p.allSurfaces);
        if (k <= 0) continue;
        const n = c.normal;
        const nUp = n.dot(ctx.state.up);
        if (nUp <= -WALL_UP_DOT) continue; // no coupling to ceilings: it could hold the player against gravity
        // On wall-like surfaces, steering away separates. On floor-like
        // surfaces (humps, crests) running forward points "away" from the
        // normal's planar part, so there separation is by jumping only.
        if (ctx.intent.wishStrength > 0 && Math.abs(nUp) < WALL_UP_DOT) {
          planarN.copy(n).projectOnPlane(ctx.state.up).normalize();
          if (ctx.intent.wishDir.dot(planarN) > p.separateInput) continue;
        }
        const st = tangential.copy(v).projectOnPlane(n).length();
        if (st < p.minTangentialSpeed) continue;
        const a = p.strength * k * proximityWeight(c.distance, p.range) * (st / (st + p.speedRef));
        // Never pull much harder than needed to close the gap and cancel
        // separation this tick. At contact only a gravity-sized residual
        // remains (≈0.18 m/s per tick), far below impact thresholds, so the
        // surface still registers as ground without grinding hits every tick.
        const dt = ctx.dt;
        const cap = Math.max(0, c.distance - ctx.movement.skin) / (dt * dt) + Math.max(0, v.dot(n)) / dt + ctx.movement.gravity;
        pull.addScaled(n, -Math.min(a, cap));
      }
      const speed = v.length();
      if (speed > 1e-6) {
        vHat.copy(v).scale(1 / speed);
        pull.addScaled(vHat, -pull.dot(vHat));
        pull.addScaled(vHat, -(pull.lengthSq() * ctx.dt) / (2 * speed));
      }
      out.add(pull);
    },
  };
}
